import React, { useState, useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import { DayPicker } from 'react-day-picker';
import { format, differenceInDays, isFuture, startOfToday } from 'date-fns';
import { getUserTrips, createCustomActivity, confirmBooking } from '../services/api';
import cacheService from '../services/cacheService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import Toast from './Toast';
import { AuthContext } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import { PayPalButtons } from "@paypal/react-paypal-js";
import LoadingSpinner from './LoadingSpinner';
import { useHistory } from 'react-router-dom';
import PayPalHotelProvider from './PayPalHotelProvider';
import { usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const BookingContainer = styled.div`
  padding: 20px;
  margin: 10px;
  position: relative;
  height: 100%;
  overflow-y: auto;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

const StepTitle = styled.h2`
  color: #333;
  font-size: 18px;
  margin-top: 30px;
`;

const FormSection = styled.div`
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 5px;
`;

const Label = styled.label`
  display: block;
  margin-top: 20px;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
`;

const RadioGroup = styled.div`
  display: flex;
  justify-content: center; // Center the radio buttons
  gap: 30px; // Increased gap between options
  margin: 30px 0 15px 0;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  color: ${props => props.disabled ? '#666' : 'inherit'};
`;

const SubTitle = styled.p`
  color: #666;
  font-size: 14px;
  margin: 5px 0;
`;

const DateRangeContainer = styled.div`
  margin: 15px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  .rdp-months {
    justify-content: center;
  }
  .rdp-nav {
    position: absolute;
    right: 1em;
    top: 1em;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 15px;
`;


const RoomCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  background-color: #f8f8f8;
`;

const RoomInfo = styled.div`
  flex: 2;
  h3 {
    margin: 0 0 5px 0;
  }
  p {
    margin: 0;
  }
`;

const RoomPrice = styled.div`
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const PricePerNight = styled.span`
  font-size: 12px;
  font-weight: normal;
`;

const RoomQuantity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;


const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 10px;
`;

const NextButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #316C6E;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const InlineFormGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const BackToFormButton = styled.button`
  background: none;
  border: none;
  color: #316C6E;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 20px;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const InvoiceItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 5px 0;
  border-bottom: ${props => props.total ? '2px solid #333' : '1px solid #ddd'};
  font-weight: ${props => props.total ? 'bold' : 'normal'};
  color: ${props => props.highlight ? '#316C6E' : 'inherit'};
`;


const BookingButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background-color: #316C6E;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #254F51;
  }
`;


const GuestCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const CounterButton = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #316C6E;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  padding: 0;

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;


const TermsLink = styled.a`
  color: #316C6E;
  text-decoration: underline;
  cursor: pointer;
`;

const formatMoney = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount) + ' ' + currency;
};

// Add these styled components (similar to POIDetailsSidePanel)
const DropdownMenu = styled.div`
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  max-height: 300px;
  overflow-y: auto;
  min-width: 200px;
  margin-top: 10px;
`;

const TripOption = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  cursor: pointer;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  span.trip-name {
    font-weight: 500;
  }
  
  span.trip-dates {
    font-size: 12px;
    color: #666;
  }
`;

const Button = styled.button`
  padding: 10px 15px;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #f8f8f8;
  }
`;

// Add these styled components for the skeleton loading
const SkeletonPulse = styled.div`
  animation: pulse 1.5s ease-in-out infinite;
  background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%);
  background-size: 400% 400%;
  
  @keyframes pulse {
    0% { background-position: 0% 0%; }
    100% { background-position: -135% 0%; }
  }
`;

const SkeletonLine = styled(SkeletonPulse)`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  margin-bottom: ${props => props.mb || '10px'};
  border-radius: 4px;
`;

const SkeletonCircle = styled(SkeletonPulse)`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 10px;
`;

const SkeletonRadioGroup = styled.div`
  display: flex;
  gap: 30px;
  margin: 30px 0 15px 0;
`;

const SkeletonRadioOption = styled.div`
  display: flex;
  align-items: center;
`;

const GuestCard = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  background-color: #f8f8f8;
  width: 100%;
`;

const GuestCardTitle = styled.h4`
  margin: 0 0 10px 0;
  color: #316C6E;
`;

const GuestCardInput = styled.input`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
`;

const GuestCardRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

// Add these styled components at the top with the others:

const InvoiceSummary = styled.div`
  background-color: #f8f8f8;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const TermsAcceptance = styled.div`
  margin: 20px 0;
`;

const TermsCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const TermsText = styled.span`
  font-size: 10px;
  color: #666;
`;

// Add these styled components
const PaymentContainer = styled.div`
    padding: 20px;
    max-width: 600px;
    margin: 0 auto;

    /* Add this to ensure PayPal styles don't override our button */
    button.premium-booking-button {
        width: 100% !important;
        padding: 12px 24px !important;
        background-color: #316C6E !important;
        color: white !important;
        border: none !important;
        border-radius: 4px !important;
        cursor: pointer !important;
        margin-top: 20px !important;
        font-size: 16px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 10px !important;
        
        &:hover:not(:disabled) {
            background-color: #254F51 !important;
        }
        
        &:disabled {
            background-color: #ccc !important;
            cursor: not-allowed !important;
            opacity: 0.7 !important;
        }
    }
`;

const PaymentHeader = styled.h2`
    color: #333;
    margin-bottom: 20px;
    text-align: center;
`;

const PaymentSubtext = styled.p`
    color: #666;
    margin-bottom: 30px;
    text-align: center;
`;

// Add these styled components at the top
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingText = styled.p`
  margin-top: 20px;
  color: #316C6E;
  font-size: 1.1rem;
`;

// Add this new styled component with the existing styled components
const BookingTitle = styled.h1`
  color: #333;
  font-size: 24px;
  margin-bottom: 20px;
`;

// Add this styled component with the others at the top
const PremiumBookingButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background-color: #316C6E;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &:hover:not(:disabled) {
    background-color: #254F51;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

// Add these new styled components
const PremiumContainer = styled.div`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const PremiumButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background-color: #316C6E;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &:hover:not(:disabled) {
    background-color: #254F51;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

// Add this helper function near the top of the file, after imports and before styled components
const getDayDifference = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
};

const PaymentStep = ({ 
  isPremium, 
  bookingFee, 
  roomCost, 
  handlePremiumBooking, 
  isProcessing,
  selectedRoom,
  onApprove,
  onError,
  onCancel,
  tripType,
  tripDates,
  hotelDates,
  sameAsTripDates,
  guestCount,
  hotel,
  selectedTripId,
  history
}) => {
  const { language } = useLanguage();
  const [isPaypalWindowOpen, setIsPaypalWindowOpen] = useState(false);
  const [{ isPending }] = usePayPalScriptReducer();

  const createActivities = async (tripId) => {
    // Create check-in activity
    const checkInActivity = {
      name: `${selectedRoom.hotelName} - ${translations[language].hotelBooking.checkIn}`,
      description: `${translations[language].hotelBooking.checkInAt} ${selectedRoom.hotelName}. ${translations[language].hotelBooking.roomType}: ${selectedRoom.category}`,
      day: sameAsTripDates ? 1 : getDayDifference(tripDates.from, hotelDates.from),
      hour: 15, // 3 PM check-in
      duration: 1,
      lat: selectedRoom.hotelLat,
      lng: selectedRoom.hotelLng,
      type: 'hotel',
      order: 1,
      price: roomCost + bookingFee
    };

    // Create check-out activity
    const checkOutActivity = {
      name: `${selectedRoom.hotelName} - ${translations[language].hotelBooking.checkOut}`,
      description: `${translations[language].hotelBooking.checkOutFrom} ${selectedRoom.hotelName}. ${translations[language].hotelBooking.roomType}: ${selectedRoom.category}`,
      day: sameAsTripDates ? 
        getDayDifference(tripDates.from, tripDates.to) : 
        getDayDifference(tripDates.from, hotelDates.to),
      hour: 11, // 11 AM check-out
      duration: 1,
      lat: selectedRoom.hotelLat,
      lng: selectedRoom.hotelLng,
      type: 'hotel',
      order: 1
    };

    // Create both activities
    await Promise.all([
      createCustomActivity(tripId, checkInActivity),
      createCustomActivity(tripId, checkOutActivity)
    ]);
  };

  const handlePayPalApprove = async (data, actions) => {
    try {
      setIsPaypalWindowOpen(true);
      const orderData = await actions.order.capture();

      // Create trip if new
      let tripId = selectedTripId;
      if (tripType === 'new') {
        const tripData = {
          name: `${translations[language].hotelBooking.tripTo} ${selectedRoom.hotelName}`,
          startDate: tripDates.from,
          endDate: tripDates.to,
          numPeople: guestCount,
          pois: [hotel.id]
        };
        const newTrip = await cacheService.createTrip(tripData);
        tripId = newTrip.id;
      }

      // Create activities before confirming booking
      await createActivities(tripId);

      // Continue with the booking confirmation
      await onApprove(data, { 
        ...actions, 
        order: { capture: () => orderData },
        tripId  // Pass tripId to onApprove
      });

      // Redirect to trip details page
      history.push(`/trip/${tripId}`);

    } catch (error) {
      if (error.message.includes('Target window is closed')) {
        onError(new Error(translations[language].hotelBooking.paymentWindowClosed));
      } else {
        onError(error);
      }
    } finally {
      setIsPaypalWindowOpen(false);
    }
  };

  return (
    <PaymentContainer>
      <PaymentHeader>{translations[language].hotelBooking.paymentDetails}</PaymentHeader>
      <PaymentSubtext>
        {isPremium ? (
          translations[language].hotelBooking.premiumDiscount
        ) : (
          `${translations[language].hotelBooking.bookingFeeMessage} ${formatMoney(bookingFee)}. 
          ${translations[language].hotelBooking.dueAtPropertyMessage} ${formatMoney(roomCost)}.`
        )}
      </PaymentSubtext>
      
      {isPremium ? (
        <PremiumButton 
          onClick={handlePremiumBooking} 
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <LoadingSpinner size="small" />
              {translations[language].common.loading}
            </>
          ) : (
            translations[language].hotelBooking.proceedToPayment
          )}
        </PremiumButton>
      ) : (
        <>
          {isPending ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <LoadingSpinner />
              <div>{translations[language].hotelBooking.loadingPayment}</div>
            </div>
          ) : window.paypal ? (
            <PayPalButtons
              forceReRender={[bookingFee]}
              createOrder={(data, actions) => {
                if (isPaypalWindowOpen) {
                  throw new Error(translations[language].hotelBooking.paymentWindowError);
                }
                return actions.order.create({
                  purchase_units: [{
                    amount: {
                      value: bookingFee.toFixed(2),
                      currency_code: selectedRoom.price.currency || 'USD'
                    },
                    description: `${translations[language].hotelBooking.bookingFee} ${selectedRoom.hotelName}`
                  }],
                  application_context: {
                    shipping_preference: 'NO_SHIPPING',
                    user_action: 'PAY_NOW'
                  }
                });
              }}
              onApprove={handlePayPalApprove}
              onError={(err) => {
                console.error('PayPal Error:', err);
                onError(err);
                setIsPaypalWindowOpen(false);
              }}
              onCancel={() => {
                onCancel();
                setIsPaypalWindowOpen(false);
              }}
              style={{
                layout: 'vertical',
                color: 'blue',
                shape: 'rect',
                label: 'pay'
              }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div>{translations[language].hotelBooking.paypalError}</div>
            </div>
          )}
        </>
      )}
    </PaymentContainer>
  );
};

const HotelBookingForm = ({ room: selectedRoom, poi: hotel, onBack, onComplete, onClose }) => {
  const { language } = useLanguage();
  const [step, setStep] = useState(1);
  const [tripType, setTripType] = useState('');
  const [existingTrips, setExistingTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState('');
  const [tripDates, setTripDates] = useState({ from: undefined, to: undefined });
  const [hotelDates, setHotelDates] = useState({ from: undefined, to: undefined });
  const [sameAsTripDates, setSameAsTripDates] = useState(null);
  const [guestCount, setGuestCount] = useState(1); // Changed from 0 to 1
  const [roomCount, setRoomCount] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const [toastLink, setToastLink] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [showTripDropdown, setShowTripDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingAuthAction, setPendingAuthAction] = useState(null);
  const { user, isAuthenticated } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [guestInfo, setGuestInfo] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const isPremium = user?.subscription_status === 'premium';
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // ... (keep existing form state)
  });
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const history = useHistory();

  // Add this function for success handling
  const onSuccess = (booking) => {
    if (onComplete) {
      onComplete({
        booking,
        tripType,
        tripId: selectedTripId,
        withBooking: true
      });
    }
    if (onClose) {
      onClose();
    }
  };

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowTripDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update the existing trips fetch
  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const trips = await getUserTrips();
        // Filter trips that end today or in the future
        const futureTrips = trips.filter(trip => 
          isFuture(new Date(trip.end_date)) || 
          new Date(trip.end_date).toDateString() === new Date().toDateString()
        );
        setExistingTrips(futureTrips);
        
        // If no trips available, force 'new' trip type
        if (futureTrips.length === 0 && tripType === 'existing') {
          setTripType('new');
        }
      } catch (error) {
        console.error('Error fetching trips:', error);
        // If error fetching trips, force 'new' trip type
        setTripType('new');
        setExistingTrips([]);
      }
    };
    fetchTrips();
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const trips = await getUserTrips();
        const sortedTrips = trips.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        setExistingTrips(sortedTrips);
      } catch (error) {
        console.error('Error fetching trips:', error);
        setTripType('new');
        setExistingTrips([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (guestCount > 0) {
      const newGuestInfo = Array(guestCount).fill().map((_, index) => ({
        firstName: index === 0 && isAuthenticated && user ? user.firstName : '',
        lastName: index === 0 && isAuthenticated && user ? user.lastName : '',
        email: index === 0 && isAuthenticated && user ? user.email : '',
        phone: index === 0 && isAuthenticated && user ? user.phone || '' : '',
        address: index === 0 && isAuthenticated && user ? user.address || '' : '',
        countryOfOrigin: index === 0 && isAuthenticated && user ? user.countryOfOrigin || '' : '',
      }));
      setGuestInfo(newGuestInfo);
    }
  }, [guestCount, isAuthenticated, user]);

  const calculateTotalCost = () => {
    const nights = differenceInDays(hotelDates.to || tripDates.to, hotelDates.from || tripDates.from);
    const roomCost = parseFloat(selectedRoom.price.amount) * nights * roomCount;
    const bookingFee = isPremium ? 0 : 10; // No booking fee for premium users
    return {
      roomCost,
      bookingFee,
      total: roomCost + bookingFee
    };
  };

  const isGuestInfoValid = () => {
    return guestInfo.every(guest => 
      guest.firstName && 
      guest.lastName && 
      guest.email && 
      guest.phone && 
      guest.address && 
      guest.countryOfOrigin
    );
  };

  const isFormValid = () => {
    if (tripType === 'existing') {
      return selectedTripId && 
             sameAsTripDates !== null && 
             guestCount > 0 && 
             guestCount <= (selectedRoom.guests.adults * roomCount) &&
             isGuestInfoValid() &&
             termsAccepted;
    } else if (tripType === 'new') {
      const hasValidTripDates = tripDates.from && tripDates.to;
      const hasValidHotelDates = sameAsTripDates ? 
        true : 
        (hotelDates.from && hotelDates.to);
      
      return hasValidTripDates && 
             sameAsTripDates !== null && 
             hasValidHotelDates &&
             guestCount > 0 && 
             guestCount <= (selectedRoom.guests.adults * roomCount) &&
             isGuestInfoValid() &&
             termsAccepted;
    }
    return false;
  };

  const needsMoreRooms = guestCount > selectedRoom.guests.adults * roomCount;

  const handleTripSelect = (e) => {
    const selectedId = e.target.value;
    setSelectedTripId(selectedId);
    
    if (selectedId) {
      const selectedTrip = existingTrips.find(trip => trip.id === parseInt(selectedId));
      if (selectedTrip) {
        const from = new Date(selectedTrip.start_date);
        const to = new Date(selectedTrip.end_date);
        setTripDates({ from, to });
        if (sameAsTripDates) {
          setHotelDates({ from, to });
        }
      }
    }
  };

  // Update the hotel dates when trip dates change
  useEffect(() => {
    if (sameAsTripDates && tripDates.from && tripDates.to) {
      setHotelDates({ from: tripDates.from, to: tripDates.to });
    }
  }, [tripDates, sameAsTripDates]);

  const getButtonText = (action) => {
    if (action === 'tripOnly') {
      return tripType === 'new' 
        ? 'Create Trip Without Booking' 
        : 'Add to Trip Without Booking';
    } else {
      return tripType === 'new'
        ? 'Create Trip & Complete Booking'
        : 'Add to Trip & Complete Booking';
    }
  };

  const handleActionClick = (action) => {
    if (!isAuthenticated) {
      setPendingAuthAction(action);
      setShowAuthModal(true);
      return;
    }

    setPendingAction(action);
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    await handleComplete(pendingAction);
    setShowConfirmModal(false);
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    if (pendingAuthAction === 'complete') {
        setStep(2);
    } else if (pendingAuthAction === 'tripOnly') {
        setPendingAction(pendingAuthAction);
        setShowConfirmModal(true);
    }
    setPendingAuthAction(null);
  };

  const handleGuestInfoChange = (index, field, value) => {
    const updatedGuestInfo = [...guestInfo];
    updatedGuestInfo[index][field] = value;
    setGuestInfo(updatedGuestInfo);
  };

  const renderGuestCards = () => (
    <div>
      <Label>{translations[language].hotelBooking.guestInfo}</Label>
      {guestInfo.map((guest, index) => (
        <GuestCard key={index}>
          <GuestCardTitle>{index === 0 ? translations[language].hotelBooking.guest : `${translations[language].hotelBooking.guest} ${index + 1}`}</GuestCardTitle>
          <GuestCardRow>
            <GuestCardInput
              type="text"
              placeholder={translations[language].hotelBooking.firstName}
              value={guest.firstName}
              onChange={(e) => handleGuestInfoChange(index, 'firstName', e.target.value)}
              required
            />
            <GuestCardInput
              type="text"
              placeholder={translations[language].hotelBooking.lastName}
              value={guest.lastName}
              onChange={(e) => handleGuestInfoChange(index, 'lastName', e.target.value)}
              required
            />
          </GuestCardRow>
          <GuestCardRow>
            <GuestCardInput
              type="email"
              placeholder={translations[language].hotelBooking.email}
              value={guest.email}
              onChange={(e) => handleGuestInfoChange(index, 'email', e.target.value)}
              required
            />
            <GuestCardInput
              type="tel"
              placeholder={translations[language].hotelBooking.phone}
              value={guest.phone}
              onChange={(e) => handleGuestInfoChange(index, 'phone', e.target.value)}
              required
            />
          </GuestCardRow>
          <GuestCardRow>
            <GuestCardInput
              type="text"
              placeholder="Address"
              value={guest.address}
              onChange={(e) => handleGuestInfoChange(index, 'address', e.target.value)}
              required
            />
            <GuestCardInput
              type="text"
              placeholder="Country of Origin"
              value={guest.countryOfOrigin}
              onChange={(e) => handleGuestInfoChange(index, 'countryOfOrigin', e.target.value)}
              required
            />
          </GuestCardRow>
        </GuestCard>
      ))}
    </div>
  );

  const renderStep1 = () => {
    const { roomCost, bookingFee, total } = calculateTotalCost();
    const nights = differenceInDays(
        hotelDates.to || tripDates.to || new Date(), 
        hotelDates.from || tripDates.from || new Date()
    ) || 0;
    
    const showInvoice = !isNaN(roomCost) && !isNaN(bookingFee) && !isNaN(total) && nights > 0;

    const handleCompleteBookingClick = () => {
        if (!isAuthenticated) {
            setPendingAuthAction('complete');
            setShowAuthModal(true);
            return;
        }
        setStep(2);
    };

    return (
        <BookingContainer>
            <BackToFormButton onClick={handleBack}>
                <FontAwesomeIcon icon={faChevronLeft} />
                {translations[language].poi.backToPoi}
            </BackToFormButton>
            <BookingTitle>{translations[language].hotelBooking.bookingSummary}</BookingTitle>
            <FormSection>
                <RadioGroup>
                    <RadioLabel>
                        <input
                            type="radio"
                            value="new"
                            checked={tripType === 'new'}
                            onChange={(e) => {
                                setTripType(e.target.value);
                                setSelectedTripId('');
                            }}
                        />
                        {translations[language].hotelBooking.createNewTrip}
                    </RadioLabel>
                    <RadioLabel disabled={existingTrips.length === 0}>
                        <input
                            type="radio"
                            value="existing"
                            checked={tripType === 'existing'}
                            onChange={(e) => {
                                setTripType(e.target.value);
                                setTripDates({ from: undefined, to: undefined });
                            }}
                            disabled={existingTrips.length === 0}
                        />
                        {translations[language].hotelBooking.selectTrip}
                    </RadioLabel>
                </RadioGroup>

                {tripType === 'existing' && (
                    <div ref={dropdownRef}>
                        <Button 
                            onClick={() => setShowTripDropdown(!showTripDropdown)}
                            style={{ width: '100%', marginTop: '10px', textAlign: 'left' }}
                        >
                            {selectedTripId ? 
                                existingTrips.find(trip => trip.id === parseInt(selectedTripId))?.name : 
                                translations[language].hotelBooking.selectTrip}
                        </Button>
                        
                        {showTripDropdown && (
                            <DropdownMenu>
                                {existingTrips
                                    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                                    .map(trip => (
                                        <TripOption 
                                            key={trip.id} 
                                            onClick={() => {
                                                handleTripSelect({ target: { value: trip.id.toString() }});
                                                setShowTripDropdown(false);
                                            }}
                                        >
                                            <span className="trip-name">{trip.name}</span>
                                            <span className="trip-dates">
                                                {format(new Date(trip.start_date), 'MMM d')} - {format(new Date(trip.end_date), 'MMM d, yyyy')}
                                            </span>
                                        </TripOption>
                                    ))}
                            </DropdownMenu>
                        )}
                        
                        {selectedTripId && tripDates.from && tripDates.to && (
                            <SubTitle>
                                {format(tripDates.from, 'MMM d, yyyy')} - {format(tripDates.to, 'MMM d, yyyy')}
                            </SubTitle>
                        )}
                    </div>
                )}
                
                {tripType === 'new' && (
                    <DateRangeContainer>
                        <SubTitle>{translations[language].hotelBooking.tripDates}</SubTitle>
                        <DayPicker
                            mode="range"
                            selected={tripDates}
                            onSelect={(range) => {
                                setTripDates(range);
                                if (sameAsTripDates) {
                                    setHotelDates(range);
                                }
                            }}
                            numberOfMonths={1}
                            fromDate={startOfToday()}
                            disabled={[{ before: startOfToday() }]}
                        />
                    </DateRangeContainer>
                )}
            </FormSection>

            <FormSection>
                <InlineFormGroup>
                    <Label>{translations[language].hotelBooking.sameAsTripDates}</Label>
                    <RadioGroup>
                        <RadioLabel>
                            <input
                                type="radio"
                                checked={sameAsTripDates === true}
                                onChange={() => {
                                    setSameAsTripDates(true);
                                    setHotelDates(tripDates);
                                }}
                            />
                            {translations[language].common.yes}
                        </RadioLabel>
                        <RadioLabel>
                            <input
                                type="radio"
                                checked={sameAsTripDates === false}
                                onChange={() => {
                                    setSameAsTripDates(false);
                                    setHotelDates({ from: undefined, to: undefined });
                                }}
                            />
                            {translations[language].common.no}
                        </RadioLabel>
                    </RadioGroup>
                </InlineFormGroup>

                {sameAsTripDates === false && (
                    <DateRangeContainer>
                        <DayPicker
                            mode="range"
                            selected={hotelDates}
                            onSelect={setHotelDates}
                            numberOfMonths={1}
                            fromDate={tripType === 'existing' ? new Date(tripDates.from) : tripDates.from}
                            toDate={tripType === 'existing' ? new Date(tripDates.to) : tripDates.to}
                            disabled={[
                                { before: tripType === 'existing' ? new Date(tripDates.from) : tripDates.from },
                                { after: tripType === 'existing' ? new Date(tripDates.to) : tripDates.to }
                            ]}
                        />
                    </DateRangeContainer>
                )}
            </FormSection>

            <FormSection>
                <InlineFormGroup>
                    <Label>{translations[language].hotelBooking.numberOfGuests}</Label>
                    <GuestCounter>
                        <CounterButton 
                            onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                        >
                            -
                        </CounterButton>
                        <span>{guestCount}</span>
                        <CounterButton 
                            onClick={() => setGuestCount(guestCount + 1)}
                        >
                            +
                        </CounterButton>
                    </GuestCounter>
                </InlineFormGroup>
            </FormSection>

            {renderGuestCards()}

            <FormSection>
                <RoomCard>
                    <RoomInfo>
                        <h3>{selectedRoom.category}</h3>
                        <p>{translations[language].poi.rooms.maxGuests}: {selectedRoom.guests.adults}</p>
                        <RoomPrice>
                            {formatMoney(parseFloat(selectedRoom.price.amount))} <PricePerNight>{translations[language].poi.rooms.perNight}</PricePerNight>
                        </RoomPrice>
                    </RoomInfo>
                    <RoomQuantity>
                        <CounterButton onClick={() => setRoomCount(Math.max(1, roomCount - 1))}>-</CounterButton>
                        <span>{roomCount}</span>
                        <CounterButton onClick={() => setRoomCount(roomCount + 1)}>+</CounterButton>
                    </RoomQuantity>
                </RoomCard>
                {needsMoreRooms && (
                    <ErrorMessage>
                        {translations[language].hotelBooking.guestCountError}
                    </ErrorMessage>
                )}
            </FormSection>

            <FormSection>
                {showInvoice && (
                    <InvoiceSummary>
                        <InvoiceItem>
                            <span>{selectedRoom.category} x {roomCount} {translations[language].poi.rooms.rooms} x {nights} {translations[language].poi.rooms.nights}</span>
                            <span>{formatMoney(roomCost)}</span>
                        </InvoiceItem>
                        <InvoiceItem highlight>
                            <span>{translations[language].hotelBooking.bookingFee}</span>
                            <span>{formatMoney(bookingFee)}</span>
                        </InvoiceItem>
                        <InvoiceItem total>
                            <span>{translations[language].hotelBooking.total}</span>
                            <span>{formatMoney(total)}</span>
                        </InvoiceItem>
                        <InvoiceItem highlight>
                            <span>{translations[language].hotelBooking.dueAtProperty}</span>
                            <span>{formatMoney(roomCost)}</span>
                        </InvoiceItem>
                    </InvoiceSummary>
                )}

                <TermsAcceptance>
                    <TermsCheckbox>
                        <input
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            required
                        />
                        <TermsText>
                            {translations[language].hotelBooking.termsAgreement}{' '}
                            <TermsLink href="/terms" target="_blank">{translations[language].hotelBooking.termsOfService}</TermsLink>
                            {'.'}
                        </TermsText>
                    </TermsCheckbox>
                </TermsAcceptance>

                <BookingButton 
                    onClick={handleCompleteBookingClick} 
                    disabled={!isFormValid() || !termsAccepted}
                >
                    {translations[language].hotelBooking.proceedToPayment}
                </BookingButton>
            </FormSection>
        </BookingContainer>
    );
};

const renderSkeleton = () => (
    <BookingContainer>
      <FormSection>
        <SkeletonLine width="150px" height="24px" mb="20px" />
        <SkeletonRadioGroup>
          <SkeletonRadioOption>
            <SkeletonCircle />
            <SkeletonLine width="100px" height="20px" mb="0" />
          </SkeletonRadioOption>
          <SkeletonRadioOption>
            <SkeletonCircle />
            <SkeletonLine width="120px" height="20px" mb="0" />
          </SkeletonRadioOption>
        </SkeletonRadioGroup>
      </FormSection>

      <FormSection>
        <SkeletonLine width="180px" height="20px" mb="15px" />
        <SkeletonLine width="100%" height="40px" mb="15px" />
        <SkeletonLine width="90%" height="15px" mb="10px" />
        <SkeletonLine width="85%" height="15px" mb="10px" />
        <SkeletonLine width="80%" height="15px" mb="20px" />
      </FormSection>

      <FormSection>
        <SkeletonLine width="150px" height="20px" mb="15px" />
        <SkeletonLine width="100%" height="200px" mb="20px" />
      </FormSection>
    </BookingContainer>
);

// Add this function with the other handlers
const handleComplete = async (action) => {
    try {
        let tripId;
        
        if (tripType === 'new') {
            // Create new trip
            const tripData = {
                name: `Trip to ${selectedRoom.hotelName}`,
                startDate: tripDates.from,
                endDate: tripDates.to,
                pois: [],
                numPeople: guestCount
            };
            const newTrip = await cacheService.createTrip(tripData);
            tripId = newTrip.id;
            
            setToastMessage('Trip created successfully!');
            setToastType('success');
            setToastLink({
                url: `/trip/${newTrip.id}`,
                text: '[View Trip]'
            });
        } else {
            tripId = selectedTripId;
            setToastMessage('Added to trip successfully!');
            setToastType('success');
            setToastLink({
                url: `/trip/${selectedTripId}`,
                text: '[View Trip]'
            });
        }

        if (action === 'complete') {
            setStep(2);
            return;
        }

        setShowToast(true);
        onComplete({ 
            tripType, 
            tripId, 
            withBooking: action === 'complete'
        });
    } catch (error) {
        console.error('Error handling action:', error);
        setToastMessage('Error: ' + (error.response?.data?.error || 'Something went wrong'));
        setToastType('error');
        setToastLink(null);
        setShowToast(true);
    }
};

// Add this helper function if not already present
const getDayDifference = (startDate, endDate) => {
    return Math.floor((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;
};

const handleBack = () => {
    if (onBack) {
        onBack();
    }
};

const calculateTotalAmount = () => {
    const roomCost = selectedRoom.price.amount * roomCount * getDayDifference(hotelDates.from, hotelDates.to);
    const bookingFee = isPremium ? 0 : (roomCost * 0.10); // No booking fee for premium users
    return {
        roomCost,
        bookingFee,
        total: roomCost + bookingFee
    };
};

const handlePremiumBooking = async () => {
    setIsProcessing(true);
    try {
        const { roomCost, bookingFee, total } = calculateTotalCost();
        
        // Create trip if new
        let tripId = selectedTripId;
        if (tripType === 'new') {
            const tripData = {
                name: `Trip to ${selectedRoom.hotelName}`,
                startDate: tripDates.from,
                endDate: tripDates.to,
                numPeople: guestCount,
                pois: [hotel.id]
            };
            const newTrip = await cacheService.createTrip(tripData);
            tripId = newTrip.id;
        }

        // Prepare booking details
        const bookingDetails = {
            hotelId: selectedRoom.id,
            roomType: selectedRoom.category,
            checkInDate: hotelDates.from || tripDates.from,
            checkOutDate: hotelDates.to || tripDates.to,
            numberOfRooms: roomCount,
            totalAmount: total,
            currency: selectedRoom.price.currency || 'USD',
            guests: guestInfo,
            itineraryId: tripId,
            hotelName: selectedRoom.hotelName,
            hotelLat: selectedRoom.hotelLat,
            hotelLng: selectedRoom.hotelLng,
            offerId: selectedRoom.id,
            roomCost: roomCost,
            bookingFee: bookingFee
        };

        // Create a mock PayPal order ID for premium users
        const mockPaypalOrder = {
            id: `PREMIUM-${Date.now()}`,
            status: 'COMPLETED'
        };

        // Confirm booking
        const response = await confirmBooking(mockPaypalOrder.id, bookingDetails);

        if (response.success) {
            // Create check-in activity
            const checkInActivity = {
                name: `${selectedRoom.hotelName} - Check-in`,
                description: `Check-in at ${selectedRoom.hotelName}. Room type: ${selectedRoom.category}`,
                day: sameAsTripDates ? 1 : getDayDifference(tripDates.from, hotelDates.from),
                hour: 15, // 3 PM check-in
                duration: 1,
                lat: selectedRoom.hotelLat,
                lng: selectedRoom.hotelLng,
                type: 'hotel',
                order: 1,
                price: roomCost + bookingFee
            };
            
            // Create check-out activity
            const checkOutActivity = {
                name: `${selectedRoom.hotelName} - Check-out`,
                description: `Check-out from ${selectedRoom.hotelName}. Room type: ${selectedRoom.category}`,
                day: sameAsTripDates ? 
                    getDayDifference(tripDates.from, tripDates.to) : 
                    getDayDifference(tripDates.from, hotelDates.to),
                hour: 11, // 11 AM check-out
                duration: 1,
                lat: selectedRoom.hotelLat,
                lng: selectedRoom.hotelLng,
                type: 'hotel',
                order: 1
            };

            // Create both activities
            await Promise.all([
                createCustomActivity(tripId, checkInActivity),
                createCustomActivity(tripId, checkOutActivity)
            ]);

            setToastMessage('Booking confirmed successfully!');
            setToastType('success');
            
            // Navigate to trip details page
            history.push(`/trip/${tripId}`);
        }
    } catch (error) {
        console.error('Error confirming booking:', error);
        setToastMessage('Error confirming booking. Please try again.');
        setToastType('error');
        setShowToast(true);
    } finally {
        setIsProcessing(false);
    }
};

const renderPaymentStep = () => {
    const { roomCost, bookingFee, total } = calculateTotalCost();

    return (
        <PayPalHotelProvider>
            <PaymentStep
                isPremium={isPremium}
                bookingFee={bookingFee}
                roomCost={roomCost}
                handlePremiumBooking={handlePremiumBooking}
                isProcessing={isProcessing}
                selectedRoom={selectedRoom}
                tripType={tripType}
                tripDates={tripDates}
                hotelDates={hotelDates}
                sameAsTripDates={sameAsTripDates}
                guestCount={guestCount}
                hotel={hotel}
                selectedTripId={selectedTripId}
                history={history}
                onApprove={async (data, { order, tripId }) => {
                    try {
                        setIsLoading(true);
                        
                        // Prepare booking details
                        const bookingDetails = {
                            hotelId: selectedRoom.id,
                            roomType: selectedRoom.category,
                            checkInDate: hotelDates.from || tripDates.from,
                            checkOutDate: hotelDates.to || tripDates.to,
                            numberOfRooms: roomCount,
                            totalAmount: total,
                            currency: selectedRoom.price.currency || 'USD',
                            guests: guestInfo,
                            itineraryId: tripId,
                            hotelName: selectedRoom.hotelName,
                            hotelLat: selectedRoom.hotelLat,
                            hotelLng: selectedRoom.hotelLng,
                            offerId: selectedRoom.id,
                            roomCost: roomCost,
                            bookingFee: bookingFee
                        };

                        // Confirm booking
                        const response = await confirmBooking(order.id, bookingDetails);

                        if (response.success) {
                            setToastMessage('Booking confirmed successfully!');
                            setToastType('success');
                            setToastLink({
                                url: `/trip/${tripId}`,
                                text: 'View Trip'
                            });
                            
                            onComplete({ 
                                tripType, 
                                tripId, 
                                withBooking: true 
                            });
                        }
                    } catch (error) {
                        console.error('Error confirming booking:', error);
                        setToastMessage('Error confirming booking. Please try again.');
                        setToastType('error');
                        setShowToast(true);
                    } finally {
                        setIsLoading(false);
                    }
                }}
                onError={(err) => {
                    console.error('PayPal Error:', err);
                    setToastMessage('Payment failed. Please try again.');
                    setToastType('error');
                    setShowToast(true);
                }}
                onCancel={() => {
                    setToastMessage('Payment cancelled.');
                    setToastType('info');
                    setShowToast(true);
                }}
            />
        </PayPalHotelProvider>
    );
};

return (
    <>
        {isLoading && (
            <LoadingOverlay>
                <LoadingSpinner />
                <LoadingText>{translations[language].hotelBooking.processingBooking}</LoadingText>
            </LoadingOverlay>
        )}
        {isLoading ? renderSkeleton() : (step === 1 ? renderStep1() : renderPaymentStep())}
        {showToast && (
            <Toast
                message={toastMessage}
                onClose={() => {
                    setShowToast(false);
                    setToastLink(null);
                }}
                type={toastType}
                link={toastLink}
            />
        )}
        {showAuthModal && (
            <AuthModal
                onClose={() => {
                    setShowAuthModal(false);
                    setPendingAuthAction(null);
                }}
                onAuthSuccess={handleAuthSuccess}
            />
        )}
    </>
);
};

export default HotelBookingForm;
